/** Today's shared pieces, from before the library (src/ui): status chips, facts lists, notices, the dialog. Founder pages and the forms still use them. */
import { useEffect, useRef, type ReactNode } from 'react'
import { Check } from './icons'

const TONE: Record<string, string> = {
  done: 'ok', active: 'ok', running: 'run', provisioning: 'run', creating: 'run', new: 'run',
  failed: 'bad', manual: 'hand', pending: 'idle', invited: 'idle', skipped: 'mute', left: 'mute', paused: 'mute',
  destroyed: 'mute', removed: 'mute',
  // Agent presence and version (the fleet).
  online: 'ok', offline: 'bad', unknown: 'idle', behind: 'hand',
}

/** A status as a chip. Signal (red) is kept for failed: an alert that needs a person. */
export function StatusChip({ status, label }: { status: string; label?: string }) {
  return (
    <span className={`chip tone-${TONE[status] ?? 'idle'}`}>
      <i className="dot" aria-hidden="true" />
      {label ?? status}
    </span>
  )
}

export function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

export function Notice({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'bad' }) {
  return <p className={`notice notice-${tone}`} role={tone === 'bad' ? 'alert' : undefined}>{children}</p>
}

export function Section({ title, count, action, children, className }: { title: string; count?: number; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={className ? `sheet ${className}` : 'sheet'}>
      <header className="sheet-head">
        <h2>
          {title}
          {count !== undefined && <span className="n">{count}</span>}
        </h2>
        {action}
      </header>
      {children}
    </section>
  )
}

/**
 * A thin bar for a percent. With `full`, a value at or over it turns Signal: something a person should look at.
 * With a `label` it's announced as a meter; without one it's decoration beside a number that says the same.
 */
export function Meter({ value, label, full }: { value: number; label?: string; full?: number }) {
  const pct = Math.max(0, Math.min(100, value))
  return (
    <div
      className={`meter${full !== undefined && pct >= full ? ' meter-full' : ''}`}
      {...(label ? { role: 'meter', 'aria-label': label, 'aria-valuenow': Math.round(pct), 'aria-valuemin': 0, 'aria-valuemax': 100 } : { 'aria-hidden': true })}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>
}

/** Native <dialog>, opened modally while `open` is true. Escape closes through onClose. */
export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])
  return (
    <dialog ref={ref} className={wide ? 'wide' : undefined} onClose={onClose} onCancel={(e) => (e.preventDefault(), onClose())}>
      {open && (
        <div className="dlg">
          <h2>{title}</h2>
          {children}
        </div>
      )}
    </dialog>
  )
}

export function Field({ label, hint, children, error }: { label: string; hint?: ReactNode; children: ReactNode; error?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
      {error && <span className="field-err">{error}</span>}
    </label>
  )
}

/** A radio choice drawn as a card: region picks, size rows. `off` greys it and blocks picking. */
export function ChoiceCard({
  name,
  value,
  checked,
  off,
  onPick,
  title,
  children,
}: {
  name: string
  value: string
  checked: boolean
  off?: boolean
  onPick?: (value: string) => void
  title?: string
  children: ReactNode
}) {
  return (
    <label className={`choice${off ? ' off' : ''}${checked ? ' on' : ''}`} title={title}>
      <input type="radio" name={name} value={value} checked={checked} disabled={off} onChange={() => onPick?.(value)} />
      {children}
    </label>
  )
}

/** A calm wait: a slow spinner, a bold line, a plain sentence. */
export function Gate({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="gate" role="status">
      <span className="spin" aria-hidden="true" />
      <div>
        <b>{title}</b>
        {children && <p>{children}</p>}
      </div>
    </div>
  )
}

/** Something we checked ourselves and found done. */
export function Verify({ title, sub, side }: { title: ReactNode; sub?: ReactNode; side?: ReactNode }) {
  return (
    <div className="verify" role="status">
      <span className="tick ok">
        <Check />
      </span>
      <span className="v-main">
        <b>{title}</b>
        {sub && <span className="v-sub">{sub}</span>}
      </span>
      {side}
    </div>
  )
}

export function LockNote({ children }: { children: ReactNode }) {
  return (
    <p className="lock-note">
      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
        <rect x="3" y="7" width="10" height="7" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5.5 7V5.2a2.5 2.5 0 015 0V7" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      <span>{children}</span>
    </p>
  )
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="mono">{children}</span>
}
